(function (scope) {
  'use strict';
  const keys = ['model', 'capacity', 'panels'];
  function canonicalModel(value) {
    const aliases = { '500%': '500pro', '500 percent': '500pro', '500per': '500pro', '500percent': '500pro', '500 each': '500pro', '500 per': '500pro', 'AC core': 'ac-core', 'AC-Core': 'ac-core', 'AC-core': 'ac-core' };
    return Object.hasOwn(aliases, value) ? aliases[value] : value;
  }
  function compatible(option, model) {
    return Boolean(option) && (option.models.includes(model) || (!option.restricted && option.models.length === 0));
  }
  function validate(data) {
    const productsByModel = { '500': 15721722380613, '500pro': 15721722347845, 'ac-core': 16081843061061 };
    const seen = new Set();
    return data.configurations.length > 0 && data.configurations.every(configuration => {
      const identity = JSON.stringify(keys.map(key => configuration[key]));
      if (seen.has(identity) || !Object.hasOwn(productsByModel, configuration.model)) return false;
      seen.add(identity);
      return data.products[configuration.items[0]?.product]?.id === productsByModel[configuration.model];
    });
  }
  function purchaseBlock(state) {
    if (state.busy) return 'busy';
    if (state.checkoutPending) return 'checkout';
    if (state.cartIssue) return 'cart';
    if (state.needsAcknowledgment) return 'acknowledge';
    if (!state.result?.valid) return 'invalid';
    return null;
  }
  function confirmedCart(cart, items, attempt) {
    if (!attempt || !Array.isArray(cart?.items) || !Array.isArray(items) || !items.length) return false;
    const expected = new Map();
    const actual = new Map();
    for (const item of items) {
      if (!Number.isSafeInteger(Number(item.id)) || Number(item.id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) return false;
      const key = String(item.id);
      expected.set(key, (expected.get(key) || 0) + item.quantity);
    }
    for (const line of cart.items.filter(item => item.properties?._wv_configuration === attempt)) {
      if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) return false;
      const key = String(line.variant_id);
      actual.set(key, (actual.get(key) || 0) + line.quantity);
    }
    return actual.size === expected.size && [...expected].every(([key, quantity]) => actual.get(key) === quantity);
  }
  function pendingTransfer(raw) {
    if (!raw) return null;
    try {
      const receipt = JSON.parse(raw);
      if (receipt?.version === 1 && typeof receipt.attempt === 'string' && receipt.attempt.startsWith('wv-') && Array.isArray(receipt.items) && receipt.items.length) return receipt;
    } catch {}
    return { attempt: raw.startsWith('wv-') ? raw : null, items: null };
  }
  function variant(product, variantId) {
    if (!product) return null;
    if (variantId) return product.variants.find(item => String(item.id) === String(variantId)) || null;
    return product.variants.length === 1 ? product.variants[0] : null;
  }
  function matches(configuration, selection) {
    return keys.every(key => configuration[key] === selection[key]);
  }
  function supportsPanels(data, model) {
    return data.configurations.some(item => item.model === model && Number(item.panelCount) > 0);
  }
  function panelQuantity(configuration, selection) {
    return selection.panelQuantity === undefined ? Number(configuration?.panelCount || 0) : Number(selection.panelQuantity);
  }
  function cableQuantity(selection) {
    const raw = selection.dcQuantity ?? 1;
    const count = /^\d+$/.test(String(raw)) ? Number(raw) : NaN;
    return Number.isSafeInteger(count) && count >= 1 ? count : null;
  }
  function requestedConfiguration(configuration, selection) {
    const count = panelQuantity(configuration, selection);
    if (!Number.isSafeInteger(count) || count < 0 || count > 20) return null;
    return { ...configuration, panelCount: count, panelsLabel: count ? count + ' × ' + configuration.panelWp + ' Wp' : configuration.panelsLabel };
  }
  function resolve(data, selection) {
    let configuration = data.configurations.find(item => matches(item, selection));
    if (!configuration) return { valid: false, reason: 'combination', items: [], total: null };
    const requested = requestedConfiguration(configuration, selection);
    if (!requested) return { valid: false, reason: 'panel-quantity-invalid', requiresReview: true, configuration, items: [], total: null };
    if (requested.panelCount !== Number(configuration.panelCount || 0)) return { valid: false, reason: 'panel-quantity-review', requiresReview: true, configuration: requested, items: [], total: null };
    const entries = configuration.items.map(item => ({ ...item }));
    const panelEntry = entries.find(item => item.role === 'panels');
    if (panelEntry && !variant(data.products[panelEntry.product], panelEntry.variantId)) return { valid: false, reason: 'panel-unavailable', configuration, items: [], total: null };
    let requiresReview = false;
    for (const group of data.accessories) {
      const option = group.options.find(item => item.id === selection[group.id]);
      if (!option) return { valid: false, reason: 'option', items: [], total: null };
      if (!compatible(option, selection.model)) return { valid: false, reason: 'compatibility', items: [], total: null };
      if (option.product) {
        if (group.id === 'dc_cable' && cableQuantity(selection) === null) return { valid: false, reason: 'cable-quantity-invalid', configuration, items: [], total: null };
        if (option.verified !== true && option.independentPurchase !== true) requiresReview = true;
        entries.push({ product: option.product, variantId: option.variantId, quantity: option.quantity * (group.id === 'dc_cable' ? cableQuantity(selection) : 1) });
      }
    }
    const merged = new Map();
    for (const entry of entries) {
      const product = data.products[entry.product];
      const chosen = variant(product, entry.variantId);
      if (!chosen || !Number.isSafeInteger(entry.quantity) || entry.quantity < 1 || product.requires_selling_plan) return { valid: false, reason: 'mapping', configuration, items: [], total: null };
      const previous = merged.get(String(chosen.id));
      merged.set(String(chosen.id), { product, variant: chosen, quantity: entry.quantity + (previous?.quantity || 0) });
    }
    const items = [...merged.values()];
    const total = items.reduce((sum, item) => sum + item.variant.price * item.quantity, 0);
    if (!Number.isSafeInteger(total)) return { valid: false, reason: 'mapping', configuration, items: [], total: null };
    return {
      valid: items.length > 0 && !requiresReview && items.every(item => item.variant.available),
      reason: items.every(item => item.variant.available) ? requiresReview ? 'review' : null : 'unavailable',
      requiresReview,
      advisories: unverifiedOptions(data, selection).filter(item => item.option.independentPurchase === true),
      configuration,
      items,
      total
    };
  }
  function initial(data, parameters) {
    const requested = Object.fromEntries(keys.map(key => [key, parameters.get('cfg_' + key)]));
    const originalModel = requested.model;
    requested.model = canonicalModel(requested.model);
    const restored = data.configurations.find(item => matches(item, requested));
    const current = data.configurations.find(item => item.items.some(entry => entry.product === data.currentHandle));
    const configuration = restored || current || data.configurations[0];
    const selection = Object.fromEntries(keys.map(key => [key, configuration?.[key] || '']));
    let reset = parameters.get('cfg_review') === '1' || (keys.some(key => parameters.has('cfg_' + key)) && !restored);
    for (const group of data.accessories) {
      const requestedOption = group.options.find(option => option.id === parameters.get('cfg_' + group.id));
      selection[group.id] = compatible(requestedOption, selection.model) ? requestedOption.id : 'none';
      if (parameters.has('cfg_' + group.id) && selection[group.id] !== parameters.get('cfg_' + group.id)) reset = true;
    }
    if (parameters.has('cfg_v') && parameters.get('cfg_v') !== '1') reset = true;
    if (parameters.has('cfg_dcQuantity')) {
      const count = cableQuantity({ dcQuantity: parameters.get('cfg_dcQuantity') });
      selection.dcQuantity = count ?? -1;
      if (count === null) reset = true;
    }
    if (parameters.has('cfg_panelQuantity')) {
      const raw = parameters.get('cfg_panelQuantity');
      const count = /^\d+$/.test(raw) ? Number(raw) : -1;
      selection.panelQuantity = Number.isSafeInteger(count) && count >= 0 && count <= 20 ? count : -1;
      if (selection.panelQuantity === -1) reset = true;
      else selection.panels = count === 0 ? 'none' : '1840';
      if (!supportsPanels(data, selection.model)) {
        if (count !== 0) reset = true;
        delete selection.panelQuantity;
        selection.panels = 'none';
      }
    }
    return { selection, reset, migrated: Boolean(restored && requested.model !== originalModel) };
  }
  function select(data, previous, key, value) {
    const selection = { ...previous, [key]: value };
    const resets = [];
    if (key === 'dcQuantity') selection.dcQuantity = cableQuantity({ dcQuantity: value }) ?? -1;
    if (key === 'dc_cable' && value === 'none' && cableQuantity(selection) === null) delete selection.dcQuantity;
    if (key === 'panels') delete selection.panelQuantity;
    if (key === 'panelQuantity') {
      const count = /^\d+$/.test(String(value)) ? Number(value) : -1;
      selection.panelQuantity = Number.isSafeInteger(count) && count >= 0 && count <= 20 ? count : -1;
      if (selection.panelQuantity !== -1) selection.panels = count === 0 ? 'none' : '1840';
    }
    if (keys.includes(key) && !data.configurations.some(item => matches(item, selection))) {
      const candidates = data.configurations.filter(item => item[key] === value && (key === 'model' || item.model === previous.model));
      candidates.sort((first, second) => keys.filter(name => second[name] === previous[name]).length - keys.filter(name => first[name] === previous[name]).length);
      const fallback = candidates[0];
      if (fallback) for (const name of keys) {
        if (name !== key && selection[name] !== fallback[name]) resets.push(name);
        selection[name] = fallback[name];
      }
    }
    if (key === 'model' && !supportsPanels(data, selection.model)) {
      if (selection.panelQuantity) resets.push('panels');
      delete selection.panelQuantity;
    }
    for (const group of data.accessories) {
      const option = group.options.find(item => item.id === selection[group.id]);
      if (!compatible(option, selection.model)) {
        selection[group.id] = 'none';
        resets.push(group.id);
      }
    }
    return { selection, resets };
  }
  function cartItems(result, configurationId) {
    if (!result.valid) throw new Error('Invalid configuration');
    return result.items.map(item => ({ id: item.variant.id, quantity: item.quantity, properties: { _wv_configuration: configurationId } }));
  }
  function unverifiedOptions(data, selection) {
    return data.accessories.flatMap(group => {
      const option = group.options.find(item => item.id === selection[group.id]);
      return option?.product && option.verified !== true ? [{ group: group.id, option }] : [];
    });
  }
  function withoutUnverified(data, selection) {
    const next = { ...selection };
    for (const item of unverifiedOptions(data, selection)) next[item.group] = 'none';
    return next;
  }
  function offerPricing(data, result) {
    const quote = { subtotal: result.total, discount: 0, total: result.total };
    if (data.batteryOfferEnabled !== true || data.currency !== 'EUR' || !result.valid || !Number.isSafeInteger(result.total)) return quote;
    const mainIds = ['57295455027525', '57295454994757', '58830888665413'];
    const mains = result.items.filter(item => mainIds.includes(String(item.variant.id)));
    const batteries = result.items.filter(item => String(item.variant.id) === '57295455125829');
    const eligible = [...mains, ...batteries];
    if (eligible.some(item => !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.variant.price) || item.variant.price <= 0 || item.product?.requires_selling_plan)) return quote;
    if (mains.reduce((sum, item) => sum + item.quantity, 0) !== 1) return quote;
    const count = batteries.reduce((sum, item) => sum + item.quantity, 0);
    if (count < 1 || count > 5) return quote;
    const discount = Math.min([10900, 19900, 29900, 39900, 49900][count - 1], batteries[0].variant.price);
    return { subtotal: result.total, discount, total: result.total - discount };
  }
  function upgradeComparison(data, selection) {
    const current = resolve(data, selection);
    const baselineConfiguration = data.configurations.find(item => item.model === selection.model && item.panels === selection.panels && Number(item.batteryCount) === 0);
    const count = Number(current.configuration?.batteryCount);
    if (!baselineConfiguration || !Number.isSafeInteger(count) || count < 1) return null;
    const baseline = resolve(data, { ...selection, capacity: baselineConfiguration.capacity });
    if (!Number.isSafeInteger(current.total) || !Number.isSafeInteger(baseline.total)) return null;
    const extra = offerPricing(data, current).total - offerPricing(data, baseline).total;
    if (extra < 0) return null;
    return { count, extra, perBattery: extra / count, baselineCapacity: baselineConfiguration.capacityLabel };
  }
  function scene(data, selection) {
    const configuration = data.configurations.find(item => matches(item, selection));
    const layers = Object.fromEntries(['panels', 'main', 'battery', 'mount', 'meter', 'ac_cable', 'dc_cable'].map(slot => [slot, null]));
    if (!configuration) return layers;
    const images = data.sceneImages || {};
    layers.main = { source: images[selection.model] || '', label: configuration.modelLabel, quantity: 1 };
    const panelCount = panelQuantity(configuration, selection);
    const batteryCount = Number(configuration.batteryCount || 0);
    if (panelCount > 0 && panelCount <= 20) layers.panels = { source: configuration.panelImage || data.panelImage || '', label: panelCount === Number(configuration.panelCount) ? configuration.panelsLabel : panelCount + ' × ' + configuration.panelWp + ' Wp', quantity: panelCount };
    if (batteryCount > 0) layers.battery = { source: images.battery || '', label: 'B500', quantity: batteryCount };
    for (const group of data.accessories) {
      const option = group.options.find(item => item.id === selection[group.id]);
      if (!option?.product) continue;
      const product = data.products[option.product];
      const selected = variant(product, option.variantId);
      if (!selected) continue;
      layers[group.id] = { source: images[group.id] !== undefined ? images[group.id] : option.image || selected.featured_image?.src || product.featured_image?.src || product.featured_image || '', label: option.label, quantity: option.quantity * (group.id === 'dc_cable' ? cableQuantity(selection) ?? 0 : 1) };
    }
    return layers;
  }
  function referencePrice(selectedVariant, quantity = 1) {
    const price = selectedVariant?.price;
    const reference = selectedVariant?.compare_at_price;
    if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(reference) || reference <= price || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const total = reference * quantity;
    return Number.isSafeInteger(total) ? total : null;
  }
  function referenceTotal(items) {
    if (!items?.length) return null;
    const prices = items.map(item => referencePrice(item.variant, item.quantity));
    if (prices.some(price => price === null)) return null;
    const total = prices.reduce((sum, price) => sum + price, 0);
    return Number.isSafeInteger(total) ? total : null;
  }
  const engine = { keys, canonicalModel, compatible, validate, purchaseBlock, confirmedCart, pendingTransfer, variant, matches, supportsPanels, resolve, initial, select, cartItems, scene, upgradeComparison, offerPricing, unverifiedOptions, withoutUnverified, cableQuantity, referencePrice, referenceTotal };
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  else scope.WV500SeriesState = engine;
})(typeof window === 'undefined' ? globalThis : window);
